import { CHANNEL_NAMES } from '@wms/domain';
import { PACKAGING_LEVEL_NAMES, type ProductListItem, type ProductStatus } from '@wms/domain';
import { useEffect, useState } from 'react';
import { api } from '../../api';
import { fmt } from '../../format';
import { navigate } from '../../router';
import type { CredentialView } from '../../types';
import { Empty, ErrorBanner, ProductThumb, SellerChip, Spinner, useLoad } from '../../ui';
import { Icon } from '../../ui/icons';
import { useSort } from '../../ui/sort';
import { ProductLabelDialog } from './ProductLabel';
import { SellerSelect, useSellerPick } from './SellerPick';

export const STATUS_LABELS: Record<ProductStatus, string> = { active: 'Aktif', inactive: 'Pasif', archived: 'Arşiv' };
const CHANNELS: Readonly<Record<string, string>> = CHANNEL_NAMES;

/** Products picked for "Etiket bas": anyone who sees the catalog may print labels. */
export function usePicked(rows: readonly ProductListItem[]) {
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const chosen = rows.filter((p) => picked.has(p.id));
  const all = rows.length > 0 && chosen.length === rows.length;
  return {
    chosen,
    all,
    has: (id: string) => picked.has(id),
    toggle: (id: string) =>
      setPicked((was) => {
        const next = new Set(was);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      }),
    toggleAll: () => setPicked(all ? new Set() : new Set(rows.map((p) => p.id))),
  };
}

/** What a product's label needs, for the label dialog. */
export const labelItems = (products: readonly ProductListItem[]) =>
  products.map((p) => ({
    id: p.id,
    sku: p.sku,
    name: p.name,
    barcode: p.primaryBarcode,
    sellerCode: p.tenantCode,
    trackLot: p.trackLot,
    trackExpiry: p.trackExpiry,
    unitLabels: p.unitLabels,
    physical: p.physical,
  }));

/**
 * No product in the list. `fresh`: none in the catalog at all, so a seller whose store is on a
 * marketplace is offered to start from there.
 */
export function EmptyCatalog({ pick, term, fresh }: { pick: ReturnType<typeof useSellerPick>; term: string; fresh: boolean }) {
  const stores = useLoad(
    () => (fresh && pick.canEdit && pick.ready ? api<CredentialView[]>('GET', `/api/mapping/accounts${pick.query('?')}`) : Promise.resolve([] as CredentialView[])),
    [fresh, pick.tenantId],
  );
  const storeNames = [...new Set((stores.data ?? []).map((c) => CHANNELS[c.channel] ?? c.channel))].join(' ve ');
  return (
    <Empty title={term ? 'Aramaya uyan ürün yok' : 'Henüz ürün yok'}>
      {pick.canEdit && !term && storeNames ? (
        <>
          <div>{storeNames}’daki ürünlerinizi tek seferde kataloğa ekleyebilirsiniz: ad, stok kodu, barkod ve görsel oradan gelir.</div>
          <a className="btn primary" href="#/urunler/eslestirme" style={{ marginTop: 12 }}>
            <Icon name="download" size={18} /> {storeNames} ürünlerini getir
          </a>
        </>
      ) : pick.canEdit && !term ? (
        'Ürünlerinizi tek tek ekleyin ya da Excel şablonuyla toplu yükleyin.'
      ) : null}
    </Empty>
  );
}

/** The catalog: products with barcode, desi, packaging levels, tracking and stock; the way to add more. */
export function Products() {
  const pick = useSellerPick();
  const [search, setSearch] = useState('');
  const [term, setTerm] = useState('');
  const [status, setStatus] = useState<ProductStatus | ''>('active');
  useEffect(() => {
    const timer = setTimeout(() => setTerm(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);
  const query = new URLSearchParams({ ...(term ? { q: term } : {}), ...(status ? { status } : {}), ...(pick.tenantId ? { tenantId: pick.tenantId } : {}) });
  const list = useLoad(() => api<ProductListItem[]>('GET', `/api/products?${query}`), [term, status, pick.tenantId]);
  const sellers = pick.staff && !pick.tenantId;
  const sort = useSort<ProductListItem>('products', {
    product: { label: 'Ürün', value: (p) => p.name },
    ...(sellers ? { seller: { label: 'Satıcı', value: (p) => p.tenantCode } } : {}),
    barcode: { label: 'Barkod', value: (p) => p.primaryBarcode },
    desi: { label: 'Desi', value: (p) => p.desi, desc: true },
    packaging: { label: 'Paketleme', value: (p) => p.packagings.length, desc: true },
    // tracked products first either way, the untracked ones last
    tracking: { label: 'Takip', value: (p) => [p.trackLot && 'Lot', p.trackExpiry && 'SKT'].filter(Boolean).join(' ') },
    stock: { label: 'Stok', value: (p) => p.available, desc: true },
    listings: { label: 'Pazaryeri', value: (p) => p.listings, desc: true },
  });
  const rows = sort.apply(list.data ?? []);
  // an empty catalog of a seller whose store is on a marketplace: start from there
  const empty = list.data?.length === 0 && !term && status !== 'inactive';
  const picked = usePicked(rows);
  const chosen = picked.chosen;
  const [labeling, setLabeling] = useState<readonly ProductListItem[] | null>(null);

  return (
    <>
      <div className="row">
        <SellerSelect pick={pick} allowAll />
        {sort.picker}
        <input className="input" style={{ maxWidth: 300 }} placeholder="SKU, ürün adı veya barkod" value={search} onChange={(e) => setSearch(e.target.value)} />
        <div className="row" style={{ gap: 6 }}>
          {(['active', 'inactive', ''] as const).map((s) => (
            <button key={s} className={`chip select${status === s ? ' on' : ''}`} onClick={() => setStatus(s)}>
              {s ? STATUS_LABELS[s] : 'Tümü'}
            </button>
          ))}
        </div>
        <span className="spacer" style={{ flex: 1 }} />
        <button className="btn" disabled={chosen.length === 0} title={chosen.length === 0 ? 'Listeden ürün seçin' : undefined} onClick={() => setLabeling(chosen)}>
          <Icon name="printer" size={18} /> Etiket bas{chosen.length > 0 ? ` (${chosen.length})` : ''}
        </button>
        {pick.canEdit && (
          <div className="row" style={{ gap: 6 }}>
            <a className="btn" href="#/urunler/eslestirme">
              <Icon name="link" size={18} /> Pazaryeri eşleştirme
            </a>
            <a className="btn" href="#/urunler/ice-aktar">
              <Icon name="upload" size={18} /> Excel / CSV ile yükle
            </a>
            <button className="btn primary" disabled={!pick.ready} title={pick.ready ? undefined : 'Önce satıcı seçin'} onClick={() => navigate('/urunler/yeni')}>
              <Icon name="plus" /> Yeni ürün
            </button>
          </div>
        )}
      </div>
      <section className="card">
        {list.error && <ErrorBanner message={list.error} onRetry={() => void list.reload()} />}
        {!list.data ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <EmptyCatalog pick={pick} term={term} fresh={empty} />
        ) : (
          <div className="table-wrap">
            <table className="t stack-sm">
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <input type="checkbox" aria-label="Tümünü seç" checked={picked.all} onChange={picked.toggleAll} />
                  </th>
                  {sort.th('product')}
                  {sellers && sort.th('seller')}
                  {sort.th('barcode')}
                  {sort.th('desi', 'num')}
                  {sort.th('packaging', 'hide-sm')}
                  {sort.th('tracking', 'hide-sm')}
                  {sort.th('stock', 'num')}
                  {sort.th('listings', 'num hide-sm')}
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.id} className="clickable" onClick={() => navigate(`/urunler/${p.id}`)}>
                    <td onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" aria-label={`${p.sku} seç`} checked={picked.has(p.id)} onChange={() => picked.toggle(p.id)} />
                    </td>
                    <td>
                      <div className="product compact">
                        <ProductThumb name={p.name} imageUrl={p.imageUrl} small />
                        <div style={{ minWidth: 0 }}>
                          <div className="name">{p.name}</div>
                          <div className="muted mono" style={{ fontSize: 13 }}>
                            {p.sku}
                            {p.status !== 'active' && <span className="chip" style={{ marginLeft: 6 }}>{STATUS_LABELS[p.status]}</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    {sellers && (
                      <td>
                        <SellerChip code={p.tenantCode} />
                      </td>
                    )}
                    <td className="mono">{p.primaryBarcode ?? <span className="chip warn">Barkod yok</span>}</td>
                    <td className="num" data-label="Desi">{p.desi === null ? <span className="chip warn">Ölçü yok</span> : fmt.dec(p.desi)}</td>
                    <td className="hide-sm">
                      <span className="row" style={{ gap: 4 }}>
                        <span className="chip">Adet</span>
                        {p.packagings.map((k) => (
                          <span key={k.level} className="chip info">
                            {PACKAGING_LEVEL_NAMES[k.level]} {k.unitsPerPack}
                          </span>
                        ))}
                      </span>
                    </td>
                    <td className="hide-sm">
                      {p.trackLot && <span className="chip">Lot</span>} {p.trackExpiry && <span className="chip">SKT</span>}
                      {!p.trackLot && !p.trackExpiry && <span className="muted">—</span>}
                    </td>
                    <td className="num" data-label="Stok">
                      <b>{fmt.n(p.available)}</b>
                      {p.physical !== p.available && <div className="muted" style={{ fontSize: 12 }}>{fmt.n(p.physical)} fiziksel</div>}
                    </td>
                    <td className="num hide-sm">{p.listings > 0 ? <span className="chip ok">{p.listings}</span> : <span className="muted">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {labeling && (
        <ProductLabelDialog products={labelItems(labeling)} onClose={() => setLabeling(null)} />
      )}
    </>
  );
}
